import {
  Component,
  Input,
  AfterViewInit,
  forwardRef,
  HostListener,
  HostBinding,
  ElementRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

export interface SelectOption {
  label: string;
  value: string;
  /** Extra text (e.g. an alternate-language name) matched on search but not shown in the label. */
  searchText?: string;
}

@Component({
  selector: 'app-multi-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true
    }
  ],
  template: `
    <!-- ── Inline mode (used inside modals) ── -->
    <ng-container *ngIf="inline">
      <div class="ms-inline-search">
        <input type="text" class="form-control form-control-sm"
               placeholder="{{ placeholder }}" [(ngModel)]="searchText">
      </div>
      <div class="ms-inline-list">
        <label class="option-item" *ngFor="let opt of filteredOptions"
               (click)="$event.stopPropagation()">
          <input type="checkbox" [checked]="isSelected(opt.value)"
                 (change)="toggleOption(opt.value)">
          <span>{{ opt.label }}</span>
        </label>
        <div class="no-options" *ngIf="filteredOptions.length === 0">No options found</div>
      </div>
    </ng-container>

    <!-- ── Normal dropdown mode ── -->
    <ng-container *ngIf="!inline">
      <div class="multi-select-wrapper" [class.is-invalid]="isInvalid">
        <div class="multi-select-trigger" (click)="toggleDropdown($event)"
             [class.open]="isOpen" [class.disabled]="disabled">
          <span class="trigger-text" *ngIf="selectedValues.length === 0">{{ placeholder }}</span>
          <span class="trigger-text" *ngIf="selectedValues.length > 0">
            {{ selectedValues.length }} item{{ selectedValues.length > 1 ? 's' : '' }} selected
          </span>
          <i class="pe-7s-angle-down caret-icon" [class.rotated]="isOpen"></i>
        </div>

        <div class="multi-select-dropdown" *ngIf="isOpen">
          <div class="search-box">
            <input type="text" class="form-control form-control-sm"
                   placeholder="Search..." [(ngModel)]="searchText"
                   (click)="$event.stopPropagation()">
          </div>
          <div class="option-list">
            <label class="option-item" *ngFor="let opt of filteredOptions"
                   (click)="$event.stopPropagation()">
              <input type="checkbox" [checked]="isSelected(opt.value)"
                     (change)="toggleOption(opt.value)">
              <span>{{ opt.label }}</span>
            </label>
            <div class="no-options" *ngIf="filteredOptions.length === 0">
              No options found
            </div>
          </div>
        </div>
      </div>

      <div class="selected-chips mt-1" *ngIf="selectedValues.length > 0">
        <span class="chip" *ngFor="let val of selectedValues">
          {{ getLabelForValue(val) }}
          <button type="button" class="chip-remove" (click)="removeOption(val)" [disabled]="disabled">
            &times;
          </button>
        </span>
      </div>
    </ng-container>
  `,
  styles: [`
    .multi-select-wrapper {
      position: relative;
    }
    .multi-select-trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.375rem 0.75rem;
      border: 1px solid #ced4da;
      border-radius: 0.375rem;
      background: #fff;
      cursor: pointer;
      min-height: 38px;
      font-size: 0.95rem;
      color: #495057;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    .multi-select-trigger:hover { border-color: #86b7fe; }
    .multi-select-trigger.open {
      border-color: #86b7fe;
      box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
    }
    .multi-select-trigger.disabled { background: #e9ecef; cursor: not-allowed; }
    .multi-select-wrapper.is-invalid .multi-select-trigger { border-color: #dc3545; }
    .trigger-text { color: #6c757d; }
    .caret-icon { transition: transform 0.2s; font-size: 1.1rem; }
    .caret-icon.rotated { transform: rotate(180deg); }

    .multi-select-dropdown {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      background: #fff;
      border: 1px solid #dee2e6;
      border-radius: 0.375rem;
      box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.1);
      z-index: 1050;
      max-height: 260px;
      display: flex;
      flex-direction: column;
    }
    .search-box { padding: 8px 8px 4px; border-bottom: 1px solid #f0f0f0; }
    .option-list { overflow-y: auto; max-height: 200px; padding: 4px 0; }
    .option-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      cursor: pointer;
      font-size: 0.9rem;
      color: #333;
      margin: 0;
      transition: background 0.1s;
    }
    .option-item:hover { background: #f8f9fa; }
    .option-item input[type=checkbox] { width: 16px; height: 16px; cursor: pointer; accent-color: #0d6efd; }
    .no-options { padding: 12px; color: #6c757d; font-size: 0.85rem; text-align: center; }

    .selected-chips { display: flex; flex-wrap: wrap; gap: 4px; }
    .chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      background: #e7f1ff;
      border: 1px solid #b6d4fe;
      border-radius: 20px;
      font-size: 0.8rem;
      color: #084298;
    }
    .chip-remove {
      background: none;
      border: none;
      padding: 0;
      font-size: 1rem;
      line-height: 1;
      color: #084298;
      cursor: pointer;
      display: flex;
      align-items: center;
    }
    .chip-remove:hover { color: #dc3545; }

    /* Inline mode */
    .ms-inline-search {
      flex-shrink: 0;
      padding-bottom: 6px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 0;
    }
    .ms-inline-list {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 4px 0;
    }
  `]
})
export class MultiSelectComponent implements ControlValueAccessor, OnChanges, AfterViewInit {
  @Input() options: SelectOption[] = [];
  @Input() placeholder: string = 'Select...';
  @Input() isInvalid: boolean = false;
  @Input() inline: boolean = false;
  @Input() initialSearch: string = '';

  @HostBinding('style.display') get hostDisplay() { return this.inline ? 'flex' : null; }
  @HostBinding('style.flex-direction') get hostFlexDir() { return this.inline ? 'column' : null; }
  @HostBinding('style.flex') get hostFlex() { return this.inline ? '1' : null; }
  @HostBinding('style.min-height') get hostMinHeight() { return this.inline ? '0' : null; }

  selectedValues: string[] = [];
  isOpen = false;
  searchText = '';
  disabled = false;

  private onChange: (val: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {}

  ngAfterViewInit(): void {
    if (this.inline) {
      if (this.initialSearch) this.searchText = this.initialSearch;
      setTimeout(() => {
        const input = this.el.nativeElement.querySelector('input[type="text"]');
        if (input) input.focus();
      }, 80);
    }
  }

  get filteredOptions(): SelectOption[] {
    if (!this.searchText) return this.options;
    const lower = this.searchText.toLowerCase();
    return this.options.filter(o =>
      o.label.toLowerCase().includes(lower) ||
      !!o.searchText && o.searchText.toLowerCase().includes(lower)
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.el.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  toggleDropdown(event: MouseEvent): void {
    if (this.disabled) return;
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (this.isOpen) this.onTouched();
  }

  isSelected(value: string): boolean {
    return this.selectedValues.includes(value);
  }

  toggleOption(value: string): void {
    if (this.isSelected(value)) {
      this.selectedValues = this.selectedValues.filter(v => v !== value);
    } else {
      this.selectedValues = [...this.selectedValues, value];
    }
    this.onChange(this.selectedValues);
  }

  removeOption(value: string): void {
    this.selectedValues = this.selectedValues.filter(v => v !== value);
    this.onChange(this.selectedValues);
  }

  getLabelForValue(value: string): string {
    return this.options.find(o => o.value === value)?.label ?? value;
  }

  writeValue(values: string[]): void {
    this.selectedValues = values ?? [];
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }
}
